import React, { useState, useEffect } from 'react';
import { Box, Container } from '@mui/system';
import { Typography, Button, Divider, Tabs, Tab } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SmsConsentPreview from './smsConsentPreview';

const hashToTab: Record<string, number> = { support: 0, privacy: 1, terms: 2 };
const tabToHash = ['support', 'privacy', 'terms'];

const MeethTerms = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const hash = location.hash.replace('#', '');
    return hashToTab[hash] ?? 0;
  });

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const idx = hashToTab[hash];
    if (idx !== undefined) setTab(idx);
  }, [location.hash]);

  const handleTabChange = (_: React.SyntheticEvent, val: number) => {
    setTab(val);
    navigate(`/meeth/terms#${tabToHash[val]}`, { replace: true });
  };

  if (location.hash.replace('#', '') === 'sms-consent') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          bgcolor: 'background.default',
          overflowY: 'auto',
        }}
      >
        <SmsConsentPreview />
      </Box>
    );
  }

  return (
    <Box>
      <Container sx={{ py: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
          variant='outlined'
          size='small'
        >
          Back to Home
        </Button>

        <Typography variant='h4' fontWeight={700} gutterBottom>
          Legal & Support
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Last updated: July 2026
        </Typography>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label='Support' />
          <Tab label='Privacy' />
          <Tab label='Terms' />
        </Tabs>

        {tab === 0 && <SupportSection />}
        {tab === 1 && <PrivacySection />}
        {tab === 2 && <TermsSection />}

        <Divider sx={{ my: 4 }} />

        <Box sx={{ pb: 6, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant='contained' href='mailto:support@meeth.ai'>
            Contact Support
          </Button>
          <Button variant='outlined' onClick={() => navigate('/')}>
            Back to Soopra
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

const SupportSection = () => (
  <Box>
    <Typography variant='body1' color='text.secondary' sx={{ mb: 1 }}>
      We answer real emails — usually within the hour during business days.
    </Typography>
    <Typography variant='body1' sx={{ mb: 0.5 }}>
      <a href='mailto:support@meeth.ai'>support@meeth.ai</a>
    </Typography>
    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
      Include your restaurant name and (if relevant) a call timestamp or order number — it makes the fix instant.
    </Typography>

    <Typography variant='h6' fontWeight={600} sx={{ mb: 2 }}>
      Common questions
    </Typography>

    {supportFAQs.map((faq, i) => (
      <Box key={i} sx={{ mb: 2.5 }}>
        <Typography variant='subtitle1' fontWeight={600}>
          {faq.q}
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          {faq.a}
        </Typography>
      </Box>
    ))}
  </Box>
);

const PrivacySection = () => (
  <Box>
    <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
      Meeth.AI ("we," "us") builds software that helps independent restaurants take orders, answer phone calls, and manage their menus. This policy explains what we collect, why, and how we handle it.
    </Typography>

    <SubSection title='Information we collect'>
      <ul style={{ paddingLeft: '24px', margin: 0 }}>
        {privacyCollected.map((item, i) => (
          <li key={i}><Typography variant='body1' color='text.secondary'>{item}</Typography></li>
        ))}
      </ul>
    </SubSection>

    <SubSection title='How we use it'>
      <ul style={{ paddingLeft: '24px', margin: 0 }}>
        {privacyUsage.map((item, i) => (
          <li key={i}><Typography variant='body1' color='text.secondary'>{item}</Typography></li>
        ))}
      </ul>
    </SubSection>

    <SubSection title="What we don't do">
      <ul style={{ paddingLeft: '24px', margin: 0 }}>
        {privacyNonPractices.map((item, i) => (
          <li key={i}><Typography variant='body1' color='text.secondary'>{item}</Typography></li>
        ))}
      </ul>
    </SubSection>

    <SubSection title='Sharing'>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 1.5 }}>
        We share data only with vendors that operate the service on our behalf — for example, our hosting provider, our voice/telephony provider (Vapi/Twilio), our database (Supabase), and the POS systems you ask us to integrate with. Each is bound by a data-processing agreement.
      </Typography>
      <Typography variant='body1' color='text.secondary'>
        No mobile information or SMS opt-in data will be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent are not shared with any third parties.
      </Typography>
    </SubSection>

    <SubSection title='Retention'>
      <Typography variant='body1' color='text.secondary'>
        Call recordings and transcripts are retained for 90 days by default and then deleted, unless you request a longer or shorter window. Order history is retained for the life of your account so your dashboards and reports stay accurate.
      </Typography>
    </SubSection>

    <SubSection title='Your rights'>
      <Typography variant='body1' color='text.secondary'>
        You can request export or deletion of restaurant or end-customer data at any time by emailing{' '}
        <a href='mailto:support@meeth.ai'>support@meeth.ai</a>. We'll respond within 7 business days.
      </Typography>
    </SubSection>

    <SubSection title='Children'>
      <Typography variant='body1' color='text.secondary'>
        The service is not directed at children under 13. We don't knowingly collect data from them.
      </Typography>
    </SubSection>

    <SubSection title='Changes'>
      <Typography variant='body1' color='text.secondary'>
        If we update this policy materially, we'll email account owners and update the "Last updated" date at the top of this page.
      </Typography>
    </SubSection>
  </Box>
);

const TermsSection = () => (
  <Box>
    <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
      By creating an account or using Meeth.AI, you agree to these terms.
    </Typography>

    <SubSection title='The service'>
      <Typography variant='body1' color='text.secondary'>
        We provide AI-powered phone answering, QR table ordering, and POS integration tools to restaurants. The features available to you depend on your plan and on the integrations you enable.
      </Typography>
    </SubSection>

    <SubSection title='Your account'>
      <ul style={{ paddingLeft: '24px', margin: 0 }}>
        <li><Typography variant='body1' color='text.secondary'>You're responsible for the accuracy of menu, pricing, and hours information you upload.</Typography></li>
        <li><Typography variant='body1' color='text.secondary'>You're responsible for keeping account credentials safe and for all activity under your account.</Typography></li>
        <li><Typography variant='body1' color='text.secondary'>You'll comply with applicable law — including consumer-protection, food-safety, and recording-consent rules in your jurisdiction.</Typography></li>
      </ul>
    </SubSection>

    <SubSection title='Acceptable use'>
      <ul style={{ paddingLeft: '24px', margin: 0 }}>
        <li><Typography variant='body1' color='text.secondary'>Don't use the service to send spam, harass customers, or attempt to break, probe, or overload the system.</Typography></li>
        <li><Typography variant='body1' color='text.secondary'>Don't reverse-engineer the platform or resell access without a written agreement.</Typography></li>
      </ul>
    </SubSection>

    <SubSection title='SMS & text messaging'>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 1.5 }}>
        When a customer provides their phone number during an order or reservation placed through the Meeth.AI platform, they consent to receive automated transactional SMS messages from Meeth.AI on behalf of the restaurant. These messages may include order confirmation, estimated pickup or delivery time, order status updates, and other transactional notifications directly related to their order. Consent to receive SMS messages is not a condition of purchase.
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 1.5 }}>
        Message frequency varies based on order activity. Customers typically receive up to 3 messages per order (e.g., order received, order being prepared, order ready or out for delivery).
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 1.5 }}>
        Standard message and data rates may apply, depending on the customer's mobile carrier and plan.
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 1.5 }}>
        <strong>To opt out:</strong> Reply STOP to any message at any time. The customer will receive a single confirmation and no further SMS will be sent.
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 1.5 }}>
        <strong>To get help:</strong> Reply HELP to any message, or contact us at{' '}
        <a href='mailto:support@meeth.ai'>support@meeth.ai</a>.
      </Typography>
      <Typography variant='body1' color='text.secondary'>
        We do not use customer phone numbers collected through SMS opt-in for marketing purposes, and we do not share them with third parties for their own marketing use.
      </Typography>
    </SubSection>

    <SubSection title='Billing'>
      <Typography variant='body1' color='text.secondary'>
        Plans are billed monthly in advance. Usage charges (call minutes, SMS, POS sync) bill in arrears at the end of the cycle. You can cancel anytime by emailing{' '}
        <a href='mailto:support@meeth.ai'>support@meeth.ai</a>; cancellations take effect at the end of the current billing period.
      </Typography>
    </SubSection>

    <SubSection title='Service availability'>
      <Typography variant='body1' color='text.secondary'>
        We aim for high availability but don't guarantee uninterrupted service. Scheduled maintenance is announced in advance when reasonable. We're not liable for outages of upstream providers (POS systems, telephony carriers).
      </Typography>
    </SubSection>

    <SubSection title='Warranties & liability'>
      <Typography variant='body1' color='text.secondary'>
        The service is provided "as is" without warranties of any kind, express or implied. To the maximum extent allowed by law, our aggregate liability for any claim is capped at the fees you paid us in the 12 months preceding the claim. We're not liable for indirect or consequential damages.
      </Typography>
    </SubSection>

    <SubSection title='Termination'>
      <Typography variant='body1' color='text.secondary'>
        Either party can terminate the agreement at the end of a billing period for any reason. We may suspend or terminate immediately for fraud, abuse, or failure to pay.
      </Typography>
    </SubSection>

    <SubSection title='Governing law'>
      <Typography variant='body1' color='text.secondary'>
        These terms are governed by the laws of the State of California, USA, without regard to conflict-of-laws rules. Disputes will be resolved in the state or federal courts located in Santa Clara County, California.
      </Typography>
    </SubSection>

    <SubSection title='Contact'>
      <Typography variant='body1' color='text.secondary'>
        Questions about these terms? Email{' '}
        <a href='mailto:support@meeth.ai'>support@meeth.ai</a>.
      </Typography>
    </SubSection>
  </Box>
);

const SubSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const supportFAQs = [
  {
    q: "The AI didn't answer a call. What happened?",
    a: "Open your dashboard's call log — every inbound call is logged with status (answered, missed, fallback). Email us the timestamp and we'll trace it within an hour.",
  },
  {
    q: "An order didn't reach the kitchen.",
    a: "Every order is logged in your dashboard even if the POS push failed. Email us the order number and we'll re-push it manually and reconnect your POS session.",
  },
  {
    q: "How do I update menu items, prices, or 86 something?",
    a: "Sign in to app.meeth.ai, open your restaurant, and edit the menu directly. 86'd items disappear from QR ordering and the AI agent stops offering them within 60 seconds.",
  },
  {
    q: "Can I change the AI agent's voice or language?",
    a: "Yes — supported languages today are English, Hindi, Telugu, and Tamil. Email us the change you want; usually same-day.",
  },
  {
    q: "How do I cancel?",
    a: "Email support@meeth.ai with your restaurant name and we'll close the account at the end of the current billing period. No fees, no questions.",
  },
];

const privacyCollected = [
  "Restaurant account data — business name, address, phone number, POS provider, and the menu you upload.",
  "Customer interactions — phone call audio and transcripts, QR ordering sessions, and orders placed through our system.",
  "End-customer contact details captured during an order or reservation (name and phone) — used to confirm the order and nothing else.",
  "Operational logs — request metadata, error traces, and dashboard usage, used to keep the service reliable.",
];

const privacyUsage = [
  "Operate the service: route calls to the AI agent, push orders to your POS, render the dashboard.",
  "Improve quality: review failure cases (with restaurant consent) to fix bugs and tune the agent.",
  "Communicate with you: account notices, security alerts, and product updates you opted into.",
];

const privacyNonPractices = [
  "We don't sell personal data.",
  "We don't use your customers' data to advertise to them.",
  "We don't train third-party AI models on your call recordings without your written permission.",
];

export default MeethTerms;
